import { useTranslation } from 'react-i18next';
import {
	Typography,
	Card,
	CardContent,
	Stack,
	Box,
	IconButton,
	Button,
	Chip,
} from '@mui/material';
import {
	DefaultValues,
	FormProvider,
	useController,
	useForm,
} from 'react-hook-form';
import { useCallback, useEffect, useState } from 'react';
import {
	Form,
	Link,
	useNavigate,
	useParams,
	useSearchParams,
} from 'react-router-dom';
import { GridColDef } from '@mui/x-data-grid';
import {
	useGetFieldOptionUnitListQuery,
	useUpdateFieldOptionUnitDetailStatusMutation,
} from '@/api/field-option/fieldOption';
import dayjs from 'dayjs';
import { DISPLAY_DATE_TIME_FORMAT } from '@/utils/dateHelper';
import LocalDataGrid from '@/common/components/dataGrid/LocalDataGrid';
import AppRadioGroup from '@/common/components/form-inputs/AppRadioGroup';
import { FIELD_UNIT_STATUS } from '@/common/constants/common';
import { closeSnackbar, enqueueSnackbar } from 'notistack';
import CloseIcon from '@mui/icons-material/Close';

export default function UnitStatusPage() {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { fieldId } = useParams();
	const [updateFieldOptionUnitDetailStatus] =
		useUpdateFieldOptionUnitDetailStatusMutation();

	const defaultValues: DefaultValues<any> = {};
	const methods = useForm<any>({
		defaultValues,
	});

	const { data: propertySummary, isSuccess: isGetPropertySummarySuccess } =
		useGetFieldOptionUnitListQuery(
			{
				pagination: { page: 1, size: 99999 },
				searchString: '',
				sortings: [],
			},
			{
				refetchOnMountOrArgChange: true,
			}
		);

	const transformResponse = useCallback((response?: any[]) => {
		if (response == null) return;
		const newResponse: any[] = [];
		response.forEach((item) => {
			newResponse.push({
				...item,
				updatedAt: !item.updatedAt
					? '-'
					: dayjs(item.updatedAt).locale('en').format(DISPLAY_DATE_TIME_FORMAT),
			});
		});
		return newResponse;
	}, []);

	const fieldList = useController({
		name: 'fieldList',
		control: methods.control,
	});

	useEffect(() => {
		if (propertySummary) {
			if (propertySummary?.data?.items) {
				methods.setValue(
					'fieldList',
					(propertySummary?.data?.items || []).map((item: any) => {
						return { ...item, status: item.status ? true : false };
					})
				);
			}
		}
	}, [propertySummary]);

	const columns: GridColDef[] = [
		{
			field: 'code',
			headerName: t('field.code'),
			flex: 20,
		},
		{
			field: 'displayName',
			headerName: t('field.districtEn'),
			flex: 20,
		},
		{
			field: 'displayNameZh',
			headerName: t('field.districtZh'),
			flex: 20,
		},
		{
			field: 'nameEn',
			headerName: t('field.unitEn'),
			flex: 20,
		},
		{
			field: 'nameZh',
			headerName: t('field.unitZh'),
			flex: 20,
		},
		{
			field: 'addressEn',
			headerName: t('field.addressEn'),
			flex: 20,
		},
		{
			field: 'addressZh',
			headerName: t('field.addressZh'),
			flex: 20,
		},
		{
			field: 'phone',
			headerName: t('field.telNo'),
			flex: 20,
		},
		{
			field: 'fax',
			headerName: t('field.faxNo'),
			flex: 20,
		},
		{
			field: 'email',
			headerName: t('field.email'),
			flex: 20,
		},
		{
			field: 'icName',
			headerName: t('field.nameIc'),
			flex: 20,
		},
		{
			field: 'updatedAt',
			headerName: t('field.lastUpdated'),
			flex: 20,
		},
		{
			field: 'status',
			headerName: t('field.status'),
			flex: 40,
			renderCell: (params: any) => {
				const allRows = methods.watch('fieldList') || [];
				const correctIndex = allRows.findIndex(
					(x: any) => x.id === params.row.id
				);

				return (
					<AppRadioGroup
						control={methods.control}
						name={`fieldList[${correctIndex}].status`}
						row
						options={FIELD_UNIT_STATUS}
					/>
				);
			},
		},
	];

	const handleSubmit = async () => {
		const response = await updateFieldOptionUnitDetailStatus({
			updateStatusDTOList: methods.getValues('fieldList'),
		});
		if ('data' in response) {
			enqueueSnackbar(t('snackbar.updateFieledUnitStatusSuccess'), {
				variant: 'success',
				anchorOrigin: { horizontal: 'center', vertical: 'top' },
				autoHideDuration: 3000,
				action: (key) => {
					return <CloseIcon onClick={() => closeSnackbar(key)} />;
				},
			});
			navigate(`/field-management/field-management-list/unit/${fieldId}`, {
				state: { refresh: true },
			});
		}
	};

	return (
		<>
			<Box display='flex' justifyContent='space-between'>
				<Typography variant='h4'>{t('drawerListItems.unitStatus')}</Typography>
			</Box>

			<Card sx={{ marginTop: 3 }}>
				<CardContent>
					<FormProvider {...methods}>
						<Box marginTop={2}>
							<LocalDataGrid
								rows={transformResponse(methods.getValues('fieldList')) ?? []}
								columns={columns}
								getRowId={(row) => row.id}
								pagination={false}
							/>
						</Box>
					</FormProvider>
				</CardContent>
			</Card>

			<Box sx={{ display: 'flex', justifyContent: 'flex-end', marginTop: 2 }}>
				<Button
					onClick={() => {
						navigate(`/field-management/field-management-list/unit/${fieldId}`);
					}}
				>
					{t('button.cancel')}
				</Button>
				<Button type='submit' variant='contained' onClick={handleSubmit}>
					{t('button.complete')}
				</Button>
			</Box>
		</>
	);
}
